import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useBusiness } from 'hooks/useBusiness';
import { useLocalization } from 'hooks/useLocalization';
import { Box, Alert, Paper, Typography, Stack, Skeleton } from '@mui/material'
import GeneralDialog from 'components/shared/GeneralDialog';
import BookingView from 'components/booking/BookingView';
import PaymentMethodButton from 'components/booking/PaymentMethodButton';
import 'components/booking/style.css';
import { Booking } from 'interfaces/BookingInterfaces';

function BookingPage() {
	const [isLoadingBooking, setIsLoadingBooking] = useState<boolean>(true);
	const [booking, setBooking] = useState<Booking|null>(null);
	const [error, setError] = useState<string|null>(null);
	const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);
	const [errorDialogContents, setErrorDialogContents] = useState<string | null>(null);
	const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);
	const [isTermsOpen, setIsTermsOpen] = useState<boolean>(false);
	const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
	const [terms, setTerms] = useState<any[]>([]);
	const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
    const [selectedDepositOption, setSelectedDepositOption] = useState<number>(100);
	const { getStr } = useLocalization();
	const { bookingNumber, bookingToken } = useParams<{ bookingNumber?: string, bookingToken?: string }>();
	const { sendRequest } = useBusiness();
	const [expandedPaymentId, setExpandedPaymentId] = useState<number|null>(null);
	
	useEffect(() => {
		sendRequest({
			type: 'fetchBooking',
			params: {
				bookingNumber: bookingNumber,
				bookingToken: bookingToken
			},
			onSuccess: (data) => {
				console.log('booking data', data);
				
				setBooking(data.fetchBooking.booking);
				setPaymentMethods(data.fetchBooking.paymentOptions);
				setTerms(data.fetchBooking.terms);
				setSelectedDepositOption(data.fetchBooking.booking.depositOptions[0]);
			},
			onError: (errorMessage) => {
				setError(errorMessage);
			},
			onFinally: () => {
				setIsLoadingBooking(false);
			}
		});
	}, []);

	if (isLoadingBooking) {
		return (
			<Stack spacing={5} direction={{ sm: 'column', md: 'row' }}>
				<Box sx={{ flex: '1 1 50%' }}>
					<Paper>
						<Box sx={{ padding: '10px 20px 30px 20px' }}>
							<Box>
								<Skeleton sx={{ width: '70%', height: 40 }} />
								<Skeleton sx={{ width: '15%', height: 15 }} />
							</Box>
							<Box>
								<Skeleton variant="rectangular" height={184} sx={{ marginTop: '30px' }} />
								<Skeleton variant="rectangular" height={184} sx={{ marginTop: '20px' }} />
								<Skeleton variant="rectangular" height={159} sx={{ marginTop: '20px' }} />
								<Skeleton variant="rectangular" height={184} sx={{ marginTop: '20px' }} />
							</Box>
						</Box>
					</Paper>
				</Box>
				<Box sx={{ flex: '1 1 50%' }}>
					<Paper>
						<Box sx={{ padding: '10px 20px 30px 20px' }}>
							<Skeleton sx={{ width: '70%', height: 40 }} />
							<Box>
								<Skeleton variant="rectangular" height={146} sx={{ marginTop: '20px' }} />
								<Skeleton variant="rectangular" height={146} sx={{ marginTop: '20px' }} />
								<Skeleton variant="rectangular" height={146} sx={{ marginTop: '20px' }} />
								<Skeleton variant="rectangular" height={146} sx={{ marginTop: '20px' }} />
							</Box>
						</Box>
					</Paper>
				</Box>
			</Stack>
		);
	}

	if (error) {
		return <Alert severity="error">Error: { error }</Alert>;
	}

	if (!booking) return null;

	const isPayable = booking.status === 'U' || booking.status === 'D';

	return (
		<>
			<Stack spacing={5} direction={{ sm: 'column', md: 'row' }}>
				<Box sx={{ flex: isPayable ? '1 1 50%' : '1 1 100%' }}>
					<BookingView booking={booking} />
				</Box>
				{
					isPayable && (
						<Box sx={{ flex: '1 1 50%' }}>
							<Paper sx={{ p: 2, backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
								<h2 style={{ marginTop: 0 }}>{getStr('Select_payment_method')}</h2>
								<div className="payment-options-wrapper">
									{
										paymentMethods.length > 0 ? (
											paymentMethods.map((method) => (
												<PaymentMethodButton
													key={method.code}
													method={method}
													expandedPaymentId={expandedPaymentId}
													setExpandedPaymentId={setExpandedPaymentId}
													booking={booking}
													terms={terms}
													selectedDepositOption={selectedDepositOption}
													setSelectedDepositOption={setSelectedDepositOption}
													setIsTermsOpen={setIsTermsOpen}
													isTermsAccepted={isTermsAccepted}
													onTermsAccepted={setIsTermsAccepted}
													isRedirecting={isRedirecting}
													setIsRedirecting={setIsRedirecting}
													setIsErrorDialogOpen={setIsErrorDialogOpen}
													setErrorDialogContents={setErrorDialogContents}
												/>
											))
										) : (
											<Alert severity="error">{getStr('No_payment_methods_available')}</Alert>
										)
									}
								</div>
							</Paper>
						</Box>
					)
				}
			</Stack>
			<GeneralDialog
				isOpen={isTermsOpen}
				onClose={() => setIsTermsOpen(false)}
				title={getStr('Terms')}
				content={
					<Stack spacing={1}>
						{terms.map((term: any, index: number) => (
							<div key={index}>
								{
									(term && term.title) && (<Typography variant="h5" sx={{ mb: 2 }}>{term.title}</Typography>)
								}
								<Typography>
									{
										(term && term.content) ? (
											<Box sx={{ whiteSpace: 'pre-line' }}>{term.content}</Box>
										) : (
											<Box sx={{ color: 'red' }}>{getStr('Translation_incomplete')}</Box>
										)
									}
								</Typography>
							</div>
						
						))}
					</Stack>
				}
			/>
			<GeneralDialog
				type="error"
				isOpen={isErrorDialogOpen}
				onClose={() => setIsErrorDialogOpen(false)}
				title={getStr('Error')}
				content={errorDialogContents}
			/>
		</>
	);
}

export default BookingPage;